import { useState } from "react";
import { Button, Flex, message } from "antd";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import { translate } from "shared/languages";
import { downloadAnalyticsExport, saveBlobAsFile } from "shared/utils/download";
import PdfRowLimitModal from "./PdfRowLimitModal.component";
import { PageCountFor } from "./useReportPageCount";

interface ExportMenuProps {
  reportSlug: string;
  // The SAME window/threshold params the page's query currently uses
  // (range or from/to, days, expiring_soon_days, months). The server
  // re-parses them with the report's own family parser, so the workbook
  // always covers exactly what the page shows. Transient view state
  // (column search, sort toggles, pagination) deliberately has no effect
  // on the Excel export: the workbook is the canonical full-data extract,
  // with autofilter on every column for instant re-sorting.
  params?: Record<string, string | number | undefined>;
  // Absent until the page's query resolves (call sites pass `data && {...}`),
  // which is why the PDF button renders disabled rather than missing: the
  // pair must not change shape under a cursor already on its way to it. The
  // render fn dynamic-imports the pdf chunk so react-pdf never enters the
  // main bundle.
  pdf?: {
    fileName: string;
    render: (maxRows?: number) => Promise<Blob>;
    // Every report with a variable-length table sets this. `available` is how
    // many rows the PDF could hold (already bounded by MAX_PDF_ROWS), `total`
    // the report's true row count, `pagesFor` the pages a row count will
    // produce (null until `prepare` has resolved), `prepare` what the picker
    // runs on opening (see useReportPageCount). Omitting it opts the report
    // out of the picker entirely, which is what Ringkasan Pendapatan does.
    rowChoice?: {
      available: number;
      total: number;
      pagesFor: PageCountFor;
      prepare: () => Promise<void>;
    };
  };
}

// Both formats are equally reachable: the PDF is the share-ready summary and
// the Excel is the complete uncapped data, so neither is a sub-choice of the
// other. They sit side by side as two buttons of one fixed width rather than
// a split button hiding Excel behind a caret. Width (not minWidth) keeps the
// pair symmetric in both languages, sized for the longest label.
const EXPORT_BUTTON_WIDTH = 150;

// The export control mounted in every Laporan report heading's `extra`
// slot, so it sits on the title line opposite the back arrow.
const ExportMenu = ({ reportSlug, params, pdf }: ExportMenuProps) => {
  const [busy, setBusy] = useState<null | "pdf" | "xlsx">(null);
  const [rowsOpen, setRowsOpen] = useState(false);
  const [preparing, setPreparing] = useState(false);

  const renderPdf = async (maxRows?: number) => {
    if (!pdf) return;
    setBusy("pdf");
    try {
      const blob = await pdf.render(maxRows);
      saveBlobAsFile(blob, pdf.fileName);
      setRowsOpen(false);
    } catch {
      message.error(translate("analyticsPage.export.error"));
    } finally {
      setBusy(null);
    }
  };

  // Ask on every report that has rows to choose from, however few: wanting a
  // top-20 out of 35 is just as reasonable as trimming a long table. Only an
  // empty table skips the dialog, having nothing to choose. Ringkasan
  // Pendapatan opts out by passing no rowChoice at all — it is at most 12
  // month rows, so there is never anything to trim.
  // The picker opens at once and counts pages as soon as its measurer (and,
  // where the download fetches more rows than the page holds, those rows)
  // are ready; until then it shows "…" in place of the count.
  const handlePdf = () => {
    if (pdf?.rowChoice && pdf.rowChoice.available > 0) {
      setRowsOpen(true);
      setPreparing(true);
      pdf.rowChoice.prepare().finally(() => setPreparing(false));
      return;
    }
    renderPdf();
  };

  const handleExcel = async () => {
    setBusy("xlsx");
    try {
      await downloadAnalyticsExport(
        { report: reportSlug, ...params },
        `spott-${reportSlug}.xlsx`,
      );
    } catch {
      message.error(translate("analyticsPage.export.error"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Flex justify="flex-end" gap={10} wrap="wrap">
      <Button
        icon={<FilePdfOutlined />}
        style={{ width: EXPORT_BUTTON_WIDTH }}
        loading={busy === "pdf" && !rowsOpen}
        disabled={!pdf || busy === "xlsx"}
        onClick={handlePdf}
      >
        {translate("analyticsPage.export.pdf")}
      </Button>
      <Button
        icon={<FileExcelOutlined />}
        style={{ width: EXPORT_BUTTON_WIDTH }}
        loading={busy === "xlsx"}
        disabled={busy === "pdf"}
        onClick={handleExcel}
      >
        {translate("analyticsPage.export.excel")}
      </Button>
      {pdf?.rowChoice && (
        <PdfRowLimitModal
          open={rowsOpen}
          available={pdf.rowChoice.available}
          total={pdf.rowChoice.total}
          pagesFor={pdf.rowChoice.pagesFor}
          preparing={preparing}
          loading={busy === "pdf"}
          onCancel={() => setRowsOpen(false)}
          onConfirm={renderPdf}
        />
      )}
    </Flex>
  );
};

export default ExportMenu;
